import { Chip, type ChipVariant } from '@/components/ui'
import type { HistoryEntry } from '../api/types'

/** Newest-first vertical timeline of a card's history (child-table rows). */
export function RegulatoryTimeline({ entries }: { entries: HistoryEntry[] }) {
  if (!entries || entries.length === 0) {
    return <p className="m-0 text-sm text-tertiary">No activity recorded yet.</p>
  }
  return (
    <ol className="flex flex-col gap-2">
      {entries.map((e) => (
        <li
          key={e.id}
          className="flex items-start gap-3 border-l border-border-subtle pl-3"
        >
          <Chip size="xs" variant={eventVariant(e.event_type)} dot>
            {eventLabel(e.event_type)}
          </Chip>
          <div className="min-w-0 flex-1">
            <div className="text-role-metadata">
              {formatTimestamp(e.created_at)}
              {e.actor ? ` · ${e.actor}` : null}
            </div>
            {e.message ? (
              <div className="mt-0.5 text-xs text-secondary">{e.message}</div>
            ) : null}
          </div>
        </li>
      ))}
    </ol>
  )
}

function eventLabel(t: string): string {
  return (
    {
      created: 'Created',
      stage_changed: 'Stage',
      assigned: 'Assigned',
      priority_changed: 'Priority',
      note_added: 'Updated',
      email_linked: 'Email',
      response_sent: 'Response sent',
      linked_to_card: 'Linked',
      due_date_changed: 'Due date',
      archived: 'Archived',
      unarchived: 'Unarchived',
      carla_update: 'CARLA',
    }[t] ?? t
  )
}

function eventVariant(t: string): ChipVariant {
  if (t === 'created') return 'success'
  if (t === 'response_sent') return 'info'
  if (t === 'archived') return 'neutral'
  if (t === 'email_linked' || t === 'carla_update') return 'accent'
  return 'neutral'
}

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return iso
    return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    })}`
  } catch {
    return iso
  }
}
